/*
  • Copyright (c) 2023 Pegasystems Inc.
  • All rights reserved.
*/
import { from } from 'rxjs';
import { map } from 'rxjs/operators';

import { resolveRestApi } from '../../router/app-router';
import { fetch } from '../../service_broker/fetch';
import { getSubmitData, processHeaders } from '../../store/state/epics/util';
import { logAndCatchError } from '../../utils/rxjs-utils';
import type { Action, State } from '../../store/state/state-manager';
import { appendJSActionQueryParams } from '../../utils/case-utils';
import ObjectTransformer from '../../utils/object-transformer';

import { getActionNameForDxApiCall, getViewTypeForFinishAssignment } from './helper';

const FINISH_ASSIGNMENT_DXAPI_LOG_ERROR_MESSAGE = 'Error occurred during ajax call at finishAssignment Epic : ';

export const executeFinishAssignmentRequest = (
  actionInPayload: Action['payload'],
  options: { state$: { value: State } }
) => {
  const {
    assignmentID,
    actionID,
    caseID,
    context,
    additionalHeaders = {},
    outcome,
    jsActionQueryParams,
    headers: requestHeaders
  } = actionInPayload;

  const { state$ } = options;

  const viewType = getViewTypeForFinishAssignment(actionInPayload);
  const actionName = getActionNameForDxApiCall(actionInPayload);

  const requestPayload = {
    assignmentID,
    actionID,
    caseID,
    viewType
  };

  let { restApi } = resolveRestApi(actionName, requestPayload);
  const { reqType } = resolveRestApi(actionName, requestPayload);

  if (viewType && !restApi?.includes('viewType=')) {
    restApi = !restApi?.includes('?') ? `${restApi}?viewType=${viewType}` : `${restApi}&viewType=${viewType}`;
  }
  if (outcome && !restApi?.includes('outcome')) {
    restApi = !restApi?.includes('?') ? `${restApi}?outcome=${outcome}` : `${restApi}&outcome=${outcome}`;
  }
  restApi = appendJSActionQueryParams(jsActionQueryParams, restApi);

  actionInPayload.viewType = viewType;

  const { content } = getSubmitData(context);
  const caseInfoHeaders = state$.value.data[context]?.caseInfo?.headers || {};
  const { etag } = caseInfoHeaders;
  const reqHeaders = {
    'if-match': etag,
    ...requestHeaders,
    ...additionalHeaders
  };

  return from(
    fetch(
      restApi,
      {
        method: reqType,
        body: content,
        headers: reqHeaders
      },
      context
    )
  ).pipe(
    map((response) => {
      const { data, headers } = response;
      ObjectTransformer.analyseResponse(data);
      return {
        response: data,
        actionInPayload,
        headers: processHeaders({ ...reqHeaders, ...headers })
      };
    }),
    logAndCatchError(FINISH_ASSIGNMENT_DXAPI_LOG_ERROR_MESSAGE, context)
  );
};

export default executeFinishAssignmentRequest;
